import styled from '@emotion/styled/macro'
import { Box } from '@mui/material'
import { keyframes } from '@emotion/react'
import theme from 'theme'

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(800%); }
`

export const InsightSkeletonBar = styled(Box)`
  height: 74px;
  width: 100%;
  background: ${theme.colors.labelCardBg};
  border: 1px solid ${theme.colors.borderMedium};
  border-radius: ${theme.attributes.borderRadius.secondary};
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 27px;
    height: 100%;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    background: linear-gradient(
      270deg,
      rgba(48, 40, 47, 0.5) 0%,
      rgba(87, 82, 86, 0.5) 50%,
      rgba(48, 40, 47, 0.5) 100%
    );
    animation: ${shimmer} 1.5s ease-in-out infinite;
  }

  /* Matches the dashboard loading state: light placeholder with a white sweep. */
  html[data-theme='light'] & {
    background: ${theme.colors.legacyPrimaryLight3};

    &::after {
      background: linear-gradient(
        270deg,
        rgba(252, 247, 251, 0) 0%,
        rgba(252, 247, 251, 0.9) 50%,
        rgba(252, 247, 251, 0) 100%
      );
    }
  }
`

export const InsightsWrapper = styled(Box)`
  background: ${theme.colors.surfaceContainerBackground};
  border-radius: ${theme.attributes.borderRadius.tertiary};
  padding: ${theme.spacing.medium}px;
  display: flex;
  flex-direction: column;
  min-height: 364px;
  box-sizing: border-box;

  .section-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: ${theme.spacing.small}px;

    .header-left {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing.xSmall}px;

      .title-row {
        display: flex;
        align-items: center;
        gap: ${theme.spacing.xSmall}px;
        height: 28px;

        .title-icon {
          width: 20px;
          height: 20px;
          color: ${theme.colors.menuItemText};
        }

        .title-text {
          font-family: ${theme.typography.fontFamily.primary};
          font-weight: ${theme.typography.fontWeight.bold};
          font-size: ${theme.typography.fontSize.header};
          color: ${theme.colors.menuItemText};
        }
      }

      .subtitle {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.normal};
        font-size: ${theme.typography.fontSize.large};
        color: ${theme.colors.dropdownLabel};
        line-height: 16px;
        letter-spacing: 0.5px;

        html[data-theme='light'] & {
          color: ${theme.colors.textCaption};
        }
      }
    }

    .chevron-button {
      display: flex;
      align-items: center;
      height: 28px;
      cursor: pointer;
      color: ${theme.colors.menuItemText};

      svg {
        width: 18px;
        height: 18px;
      }

      &.disabled {
        cursor: default;
        opacity: 0.4;
        pointer-events: none;
      }
    }
  }

  .insight-cards {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.small}px;
  }

  .insight-card {
    background: ${theme.colors.labelCardBg};
    border: 1px solid ${theme.colors.borderMedium};
    border-radius: ${theme.attributes.borderRadius.secondary};
    padding: 12px 16px 12px 12px;
    display: flex;
    align-items: center;
    gap: ${theme.spacing.sm}px;
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;

    html[data-theme='light'] & {
      background: #fafafb;
      border-color: #f0f0f1;

      &:hover {
        background: ${theme.colors.cardFillTertiary};
        border-color: ${theme.colors.strokePrimary};
      }
    }

    &:hover {
      background: ${theme.colors.cardFillTertiary};
      border-color: ${theme.colors.strokePrimary};
    }

    .insight-icon-box {
      width: 48px;
      height: 48px;
      min-width: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${theme.colors.btnTertiaryBg};
      border-radius: ${theme.attributes.borderRadius.tertiary};

      svg {
        width: 18px;
        height: 18px;
        color: ${theme.colors.dropdownLabel};
      }
    }

    .insight-content {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .insight-label {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.normal};
        font-size: ${theme.typography.fontSize.large};
        color: ${theme.colors.devicesSecondaryText};
        line-height: 22px;
      }

      .insight-value {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.bold};
        font-size: ${theme.typography.fontSize.large};
        color: ${theme.colors.menuItemText};
        line-height: 20px;
      }
    }
  }
`
